import React, { useCallback, useState } from 'react';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Box, Button, CircularProgress } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import {
  GET_PLAYER_ATTENDANCE_SESSION,
  SUBMIT_ATTENDANCE_RESPONSE,
} from '../../../lib/queries/attendance-response.queries';
import {
  RSVP_STYLES,
  resolveRsvpLinkState,
} from '../../Attendance/attendance.helpers';
import { isUnauthorizedTokenError } from './public-edit-player.helpers';
import { useLogQueryError } from '../../../lib/hooks/use-log-query-error';
import { parentUser } from '../../../lib/services/auth.service';
import { RsvpCard } from './rsvp-card.component';
import { RsvpReasonForm } from './rsvp-reason-form.component';
import { RsvpSessionDetails } from './rsvp-session-details.component';
import { PLAYER_PAGE_CARD_MAX_WIDTH } from './public-edit-player.constants';

// Maps a raw RSVP-submission failure to a friendly, self-contained message via
// substring matching on the backend's error text — a deliberate loose coupling
// so the public flow never echoes `err.message`. Keep the most specific checks
// first; an unmatched message falls through to the generic fallback (safe
// degrade). Pair any backend error-text change with an update here.
const mapRsvpError = (err: { message?: string }): string => {
  const message = (err && err.message) || '';
  if (isUnauthorizedTokenError(err)) {
    return 'This RSVP link has expired or is no longer valid. Please open the most recent link from your email or text, or contact your organization for a new one.';
  }
  if (message.includes('roster')) {
    return "This athlete isn't on the roster for this ice time, so we couldn't record an RSVP. Please contact your organization.";
  }
  if (message.includes('not being requested') || message.includes('not found')) {
    return "We couldn't find an open RSVP for this ice time — it may have been closed or rescheduled. Please contact your organization.";
  }
  return "We couldn't record your response. Please try again in a moment, or contact your organization for help.";
};

// Neutral page-chrome accent for the non-status states (loading, picker).
const ACCENT_NEUTRAL = '#3b82f6';

const STATUS_BUTTON_LABEL: Record<AttendanceStatus, string> = {
  attending: "Yes, I'll be there",
  not_attending: "No, I can't make it",
  pending: '',
};

interface IPublicEditPlayerRsvpProps {
  playerAssessmentId: string;
  token: string | null;
  assessmentSessionId: string | null;
  statusParam: string | null;
  // Provided when the focused view was reached from the attendance "dashboard"
  // OR via a deep-link URL — clicking returns the user to the table view.
  // Optional so the component can still be embedded standalone.
  onBack?: () => void;
}

// Token-authenticated RSVP responder — the focused detail view of the public
// edit-player page. Reached from an email button deep-link (with a pre-selected
// status) or from a row tap on the attendance dashboard (no pre-selected
// status). The mutation NEVER fires on mount — only from an explicit button
// tap below. See "The public RSVP page never submits on load" in CLAUDE.md.
export const PublicEditPlayerRsvp: React.FC<IPublicEditPlayerRsvpProps> = ({
  playerAssessmentId,
  token,
  assessmentSessionId,
  statusParam,
  onBack,
}) => {
  // The table query is `cache-and-network`, so it re-fetches on every remount
  // back from this view — refetchQueries here would be a no-op anyway since
  // the table is unmounted at the moment the mutation completes.
  const [submit, { loading }] = useMutation(SUBMIT_ATTENDANCE_RESPONSE);
  // Reads the linked ice time directly rather than looking it up in the
  // upcoming list, which excludes anything already started, closed or deleted.
  const { data: sessionData, error: sessionError } = useQuery(GET_PLAYER_ATTENDANCE_SESSION, {
    variables: { playerAssessmentId, assessmentSessionId, token },
    skip: !assessmentSessionId,
    fetchPolicy: 'cache-and-network',
  });
  useLogQueryError(sessionError, 'GetPlayerAttendanceSession');
  const [result, setResult] = useState<AttendanceStatus | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // not-attending requires a reason; `collectingReason` switches the picker
  // from the two-button choice to the reason form before submitting.
  const [reason, setReason] = useState<AttendanceReason | ''>('');
  const [reasonNote, setReasonNote] = useState('');
  const [collectingReason, setCollectingReason] = useState(false);

  // PARENT JWT also satisfies the server's submitAttendanceResponse ACL.
  const hasParentAuth = !!useReactiveVar(parentUser);
  const linkState = resolveRsvpLinkState({ token, hasParentAuth, assessmentSessionId, status: statusParam });

  // Fires ONLY from an explicit button tap below — never on mount. See the
  // component docstring for why that distinction is load-bearing.
  const handleSubmit = useCallback((chosenStatus: AttendanceStatus, chosenReason?: AttendanceReason, note?: string) => {
    setErrorMessage(null);
    const notAttending = chosenStatus === 'not_attending';
    submit({
      variables: {
        _id: assessmentSessionId,
        playerAssessmentId,
        status: chosenStatus,
        reason: notAttending ? chosenReason : undefined,
        reasonNote: notAttending && chosenReason === 'other' ? note : undefined,
        token,
      },
    })
      .then((res) => {
        const saved = res && res.data && res.data.submitAttendanceResponse;
        setResult(saved ? saved.status : chosenStatus);
      })
      .catch((err) => {
        console.error('submitAttendanceResponse failed:', err);
        setErrorMessage(mapRsvpError(err));
      });
  }, [submit, assessmentSessionId, playerAssessmentId, token]);

  // Not memoized: a cheap property read, not fed to any hook dep array.
  const session = sessionData && sessionData.playerAttendanceSession;
  const details = session ? <RsvpSessionDetails session={session} /> : null;

  let content: React.ReactNode;

  if (result) {
    const isAttending = result === 'attending';
    content = (
      <RsvpCard
        accent={RSVP_STYLES[result].color}
        details={details}
        headline={isAttending ? "You're in." : 'Got it — marked as not attending.'}
        body={
          isAttending
            ? 'Thanks for confirming.'
            : 'Thanks for letting us know.'
        }
      />
    );
  } else if (errorMessage) {
    content = (
      <RsvpCard
        accent={RSVP_STYLES.not_attending.color}
        headline="We couldn't record your response."
        body={errorMessage}
      />
    );
  } else if (linkState === 'malformed') {
    content = (
      <RsvpCard
        accent={RSVP_STYLES.not_attending.color}
        headline="This link is incomplete."
        body="This RSVP link is missing some information. Please open the most recent link from your email or text, or contact your organization for help."
      />
    );
  } else if (loading) {
    content = (
      <RsvpCard accent={ACCENT_NEUTRAL} headline="Recording your response…" body="One moment." details={details}>
        <CircularProgress size={28} />
      </RsvpCard>
    );
  } else if (linkState === 'confirm') {
    // resolveRsvpLinkState only returns 'confirm' for a submittable status.
    const chosen = statusParam as AttendanceStatus;
    const isAttending = chosen === 'attending';
    content = (
      <RsvpCard
        accent={RSVP_STYLES[chosen].color}
        details={details}
        headline="Confirm your RSVP"
        body={
          isAttending
            ? 'Tap below to RSVP that your athlete will be attending this ice time.'
            : "Choose a reason your athlete won't be attending, then submit."
        }
      >
        {isAttending ? (
          <Button
            variant="contained"
            onClick={() => handleSubmit(chosen)}
            style={{ background: RSVP_STYLES[chosen].color, color: '#ffffff' }}
          >
            {STATUS_BUTTON_LABEL[chosen]}
          </Button>
        ) : (
          <RsvpReasonForm
            reason={reason}
            onReasonChange={setReason}
            reasonNote={reasonNote}
            onReasonNoteChange={setReasonNote}
            onSubmit={() => handleSubmit('not_attending', reason || undefined, reasonNote)}
            submitting={loading}
            accent={RSVP_STYLES.not_attending.color}
          />
        )}
      </RsvpCard>
    );
  } else if (collectingReason) {
    // 'pick' path, after the parent tapped "No, I can't make it" — collect the
    // required reason before submitting.
    content = (
      <RsvpCard
        accent={RSVP_STYLES.not_attending.color}
        details={details}
        headline="Why can't they make it?"
        body="Choose a reason, then submit."
      >
        <RsvpReasonForm
          reason={reason}
          onReasonChange={setReason}
          reasonNote={reasonNote}
          onReasonNoteChange={setReasonNote}
          onSubmit={() => handleSubmit('not_attending', reason || undefined, reasonNote)}
          submitting={loading}
          accent={RSVP_STYLES.not_attending.color}
        />
        <Box mt={2}>
          <Button onClick={() => setCollectingReason(false)} style={{ textTransform: 'none', color: '#374151' }}>
            Back
          </Button>
        </Box>
      </RsvpCard>
    );
  } else {
    // 'pick' — token + session, but no pre-selected status (SMS / footer link).
    content = (
      <RsvpCard
        accent={ACCENT_NEUTRAL}
        details={details}
        headline="Will your athlete be at this ice time?"
        body="Tap a response below to RSVP."
      >
        <Box display="flex" justifyContent="center" style={{ gap: '0.75rem' }}>
          <Button
            variant="contained"
            onClick={() => handleSubmit('attending')}
            style={{ background: RSVP_STYLES.attending.color, color: '#ffffff' }}
          >
            {STATUS_BUTTON_LABEL.attending}
          </Button>
          <Button
            variant="contained"
            onClick={() => setCollectingReason(true)}
            style={{ background: RSVP_STYLES.not_attending.color, color: '#ffffff' }}
          >
            {STATUS_BUTTON_LABEL.not_attending}
          </Button>
        </Box>
      </RsvpCard>
    );
  }

  return (
    <>
      {onBack && (
        <Box style={{ maxWidth: PLAYER_PAGE_CARD_MAX_WIDTH, margin: '0 auto 8px' }}>
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIosIcon style={{ fontSize: 14 }} />}
            style={{ textTransform: 'none', color: '#374151', paddingLeft: 4 }}
          >
            Back to ice times
          </Button>
        </Box>
      )}
      {content}
    </>
  );
};
